import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeftIcon } from '@untitledui-icons/react/line'
import { AuthLayout } from '@/components/auth'
import { useAuth } from '@/hooks/api'
import { forgotPasswordSchema, type ForgotPasswordFormValues } from '@/schemas/auth'
import { getErrorMessage } from '@/lib/api'

export function ForgotPasswordPage() {
  const { t } = useTranslation()
  const {
    forgotPassword,
    isSendingResetEmail,
    forgotPasswordError,
    forgotPasswordSuccess,
  } = useAuth()

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  })

  const emailValue = watch('email')

  const onSubmit = (data: ForgotPasswordFormValues) => {
    forgotPassword(data)
  }

  // Success state
  if (forgotPasswordSuccess) {
    return (
      <AuthLayout
        title={t('app.auth.check_mail')}
        subtitle={t('app.auth.if_account_exists')}
      >
        <div className="space-y-6">
          {/* Email icon */}
          <div className="flex justify-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-900/30">
              <svg
                className="h-7 w-7 text-brand-600 dark:text-brand-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
          </div>

          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            {t('app.auth.resend_code')}{' '}
            <button
              type="button"
              onClick={() => forgotPassword({ email: emailValue })}
              disabled={isSendingResetEmail}
              className="font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400"
            >
              {t('app.auth.resend_code')}
            </button>
          </p>

          <Link
            to="/login"
            className="flex items-center justify-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            {t('app.auth.back_to_login')}
          </Link>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title={t('app.auth.forgot_password')}
      subtitle={t('app.auth.send_reset_instructions')}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Email field */}
        <div>
          <label
            htmlFor="email"
            className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            {t('app.auth.email')}
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            placeholder={t('app.auth.enter_your_email')}
            className={`
              w-full rounded-lg border px-3.5 py-2.5 text-sm
              placeholder:text-gray-400
              focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-0
              disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500
              dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500
              ${
                errors.email
                  ? 'border-error-500 focus:ring-error-500'
                  : 'border-gray-300 dark:border-gray-600'
              }
            `}
            disabled={isSendingResetEmail}
            {...register('email')}
          />
          {errors.email && (
            <p className="mt-1.5 text-sm text-error-500">
              {errors.email.message}
            </p>
          )}
        </div>

        {/* Error message */}
        {forgotPasswordError && (
          <div className="rounded-lg bg-error-50 p-3 text-sm text-error-700 dark:bg-error-900/20 dark:text-error-400">
            {getErrorMessage(forgotPasswordError)}
          </div>
        )}

        {/* Submit button */}
        <button
          type="submit"
          disabled={isSendingResetEmail}
          className="
            flex w-full items-center justify-center gap-2 rounded-lg
            bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white
            transition-colors hover:bg-brand-600
            focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2
            disabled:cursor-not-allowed disabled:opacity-50
          "
        >
          {isSendingResetEmail ? (
            <>
              <svg
                className="h-4 w-4 animate-spin"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              {t('resources.generic.loading')}
            </>
          ) : (
            t('app.auth.reset_password')
          )}
        </button>

        {/* Back to login */}
        <Link
          to="/login"
          className="flex items-center justify-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          {t('app.auth.back_to_login')}
        </Link>
      </form>
    </AuthLayout>
  )
}

export default ForgotPasswordPage
